import { useQuery } from '@tanstack/react-query'
import { type ReactNode, useCallback } from 'react'

import { isApiError } from '@/api/client'
import { getMe, queryKeys } from '@/api/fpa'
import { ICONS } from '@/components/icons/icons'
import { env, MOCK_NO_ACCESS_EMAIL } from '@/config/env'

import { AuthShell } from './AuthShell'
import styles from './AuthShell.module.css'
import { signedInEmail } from './identityAuth'
import { MeContext } from './MeContext'
import { NoAccessScreen } from './NoAccessScreen'

/** Resolves `/v1/me`: 403 → no-access screen (not an FPA user), success → app with MeContext. */
export function AccessGate({ children }: { children: ReactNode }) {
  const me = useQuery({ queryKey: queryKeys.me, queryFn: ({ signal }) => getMe(signal), staleTime: Infinity })
  const { refetch } = me
  const handleRetry = useCallback(() => {
    void refetch()
  }, [refetch])

  if (me.data) return <MeContext.Provider value={me.data}>{children}</MeContext.Provider>
  if (me.error && isApiError(me.error) && me.error.isForbidden) {
    // The API's 403 names the resolved account; fall back to the id token (or the mock account).
    const fromApi = (me.error.body as { email?: unknown } | null)?.email
    const email = typeof fromApi === 'string' ? fromApi : env.useMocks ? MOCK_NO_ACCESS_EMAIL : signedInEmail()
    return <NoAccessScreen email={email} detail={me.error.serverMessage} />
  }
  if (me.error && !(isApiError(me.error) && me.error.isUnauthorized)) {
    return (
      <AuthShell icon="alert" tone="bad" title="FP&A is unavailable">
        <p className={styles.text}>We could not reach the FP&amp;A service. Check your connection and try again.</p>
        <div className={styles.actions}>
          <button type="button" className={styles.primary} onClick={handleRetry}>
            <ICONS.refresh size={14} />
            Try again
          </button>
        </div>
      </AuthShell>
    )
  }
  return (
    <AuthShell icon="shield" title="Checking your access…">
      <p className={styles.text} role="status">
        One moment while we confirm your FP&amp;A access.
      </p>
    </AuthShell>
  )
}
